import {
  type EventRegistration,
  type EventRegistrationsResponse,
  MAX_REGISTRATION_QUESTIONS,
  type RegistrationQuestionType,
  type RegistrationState,
  registrationQuestionTypeValues,
} from '@pulsify/shared/dto'
import { NetworkError } from '@pulsify/shared/network-error'
import { ArrowDown, ArrowUp, ClipboardList, Download, Plus, Trash2 } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import {
  useCancelEventRegistration,
  useEventRegistrationSettings,
  useEventRegistrations,
  useExportEventRegistrations,
  useSaveEventRegistration,
} from '~/entities/event/model/use-registrations'
import {
  answerLabel,
  configFromDraft,
  draftFromConfig,
  isChoiceType,
  moveQuestion,
  newQuestion,
  type QuestionDraft,
  type RegistrationDraft,
} from '~/features/events/lib/registration-form'
import * as m from '~/paraglide/messages'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Checkbox } from '~/shared/ui/checkbox'
import { Disclosure } from '~/shared/ui/disclosure'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shared/ui/select'
import { Switch } from '~/shared/ui/switch'
import { Textarea } from '~/shared/ui/textarea'

const TYPE_LABELS: Record<RegistrationQuestionType, () => string> = {
  short_text: m.signupTypeShortText,
  long_text: m.signupTypeLongText,
  single_choice: m.signupTypeSingleChoice,
  multiple_choice: m.signupTypeMultipleChoice,
  checkbox: m.signupTypeCheckbox,
  number: m.signupTypeNumber,
}

const STATE_LABELS: Record<RegistrationState, () => string> = {
  open: m.signupStateOpen,
  waitlist: m.signupStateWaitlist,
  full: m.signupStateFull,
  closed: m.signupStateClosed,
}

const STATUS_LABELS: Record<EventRegistration['status'], () => string> = {
  confirmed: m.signupStatusConfirmed,
  waitlist: m.signupStatusWaitlist,
  cancelled: m.signupStatusCancelled,
}

function errorMessage(error: unknown): string {
  return error instanceof NetworkError
    ? error.getDialogContent().message
    : m.unexpectedErrorOccurred()
}

/** "Anmeldung": turn sign-ups on, set the questions, seats and deadline, and see who signed up. */
export function EventRegistrationCard({ eventId }: { eventId: string }) {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const settings = useEventRegistrationSettings(businessId, eventId)
  if (!settings.data) return null
  const { config, counts, state } = settings.data

  return (
    <Card className="px-4 py-2 sm:px-6">
      <Disclosure
        title={m.signupTitle()}
        hint={
          config.enabled
            ? m.signupHintOn({
                state: STATE_LABELS[state](),
                confirmed: String(counts.confirmed),
              })
            : m.signupHintOff()
        }
        icon={<ClipboardList />}
        contentClassName="space-y-6 pb-4"
      >
        <RegistrationSettingsForm
          key={JSON.stringify(config)}
          businessId={businessId}
          eventId={eventId}
          draft={draftFromConfig(config)}
        />
        {config.enabled || counts.confirmed + counts.waitlist + counts.cancelled > 0 ? (
          <RegistrationList businessId={businessId} eventId={eventId} />
        ) : null}
      </Disclosure>
    </Card>
  )
}

function RegistrationSettingsForm({
  businessId,
  eventId,
  draft: saved,
}: {
  businessId: string
  eventId: string
  draft: RegistrationDraft
}) {
  const id = useId()
  const save = useSaveEventRegistration(businessId, eventId)
  const [draft, setDraft] = useState(saved)
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved)

  const update = (patch: Partial<RegistrationDraft>) =>
    setDraft((current) => ({ ...current, ...patch }))
  const updateQuestion = (index: number, patch: Partial<QuestionDraft>) =>
    update({
      questions: draft.questions.map((question, i) =>
        i === index ? { ...question, ...patch } : question,
      ),
    })

  const submit = () =>
    save.mutate(configFromDraft(draft), {
      onSuccess: () => toast.success(m.signupSaved()),
      onError: (error) => toast.error(errorMessage(error)),
    })

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Label htmlFor={`${id}-enabled`}>{m.signupEnable()}</Label>
          <p className="text-muted-foreground text-sm">{m.signupEnableHint()}</p>
        </div>
        <Switch
          id={`${id}-enabled`}
          checked={draft.enabled}
          onCheckedChange={(enabled) => update({ enabled })}
        />
      </div>

      {draft.enabled ? (
        <>
          <div className="space-y-3">
            <p className="font-medium text-sm">{m.signupQuestions()}</p>
            {draft.questions.length === 0 ? (
              <p className="text-muted-foreground text-sm">{m.signupNoQuestions()}</p>
            ) : null}
            {draft.questions.map((question, index) => (
              <QuestionEditor
                key={question.id}
                question={question}
                first={index === 0}
                last={index === draft.questions.length - 1}
                onChange={(patch) => updateQuestion(index, patch)}
                onMove={(delta) =>
                  update({ questions: moveQuestion(draft.questions, index, delta) })
                }
                onRemove={() =>
                  update({ questions: draft.questions.filter((_, i) => i !== index) })
                }
              />
            ))}
            <Button
              variant="outline"
              size="sm"
              disabled={draft.questions.length >= MAX_REGISTRATION_QUESTIONS}
              onClick={() => update({ questions: [...draft.questions, newQuestion()] })}
            >
              <Plus />
              {m.signupAddQuestion()}
            </Button>
            {draft.questions.length >= MAX_REGISTRATION_QUESTIONS ? (
              <p className="text-muted-foreground text-xs">
                {m.signupQuestionsMax({ max: String(MAX_REGISTRATION_QUESTIONS) })}
              </p>
            ) : null}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor={`${id}-seats`}>{m.signupSeatLimit()}</Label>
              <Input
                id={`${id}-seats`}
                type="number"
                min={1}
                inputMode="numeric"
                placeholder={m.signupSeatLimitPlaceholder()}
                value={draft.seatLimit}
                onChange={(event) => update({ seatLimit: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${id}-deadline`}>{m.signupDeadline()}</Label>
              <Input
                id={`${id}-deadline`}
                type="datetime-local"
                value={draft.deadline}
                onChange={(event) => update({ deadline: event.target.value })}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${id}-waitlist`}
              checked={draft.waitlist}
              onCheckedChange={(checked) => update({ waitlist: checked === true })}
            />
            <Label htmlFor={`${id}-waitlist`} className="font-normal">
              {m.signupWaitlist()}
            </Label>
          </div>
        </>
      ) : null}

      <div className="flex justify-end">
        <Button size="sm" disabled={!dirty || save.isPending} onClick={submit}>
          {m.signupSave()}
        </Button>
      </div>
    </div>
  )
}

function QuestionEditor({
  question,
  first,
  last,
  onChange,
  onMove,
  onRemove,
}: {
  question: QuestionDraft
  first: boolean
  last: boolean
  onChange: (patch: Partial<QuestionDraft>) => void
  onMove: (delta: -1 | 1) => void
  onRemove: () => void
}) {
  const id = useId()
  return (
    <div className="space-y-3 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-48 flex-1 space-y-1.5">
          <Label htmlFor={`${id}-label`}>{m.signupQuestionLabel()}</Label>
          <Input
            id={`${id}-label`}
            value={question.label}
            maxLength={200}
            onChange={(event) => onChange({ label: event.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-type`}>{m.signupQuestionType()}</Label>
          <Select
            value={question.type}
            onValueChange={(type) => onChange({ type: type as RegistrationQuestionType })}
          >
            <SelectTrigger id={`${id}-type`} className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {registrationQuestionTypeValues.map((type) => (
                <SelectItem key={type} value={type}>
                  {TYPE_LABELS[type]()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {isChoiceType(question.type) ? (
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-options`}>{m.signupOptions()}</Label>
          <Textarea
            id={`${id}-options`}
            rows={3}
            value={question.options}
            onChange={(event) => onChange({ options: event.target.value })}
          />
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${id}-required`}
            checked={question.required}
            onCheckedChange={(checked) => onChange({ required: checked === true })}
          />
          <Label htmlFor={`${id}-required`} className="font-normal">
            {m.signupRequired()}
          </Label>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            disabled={first}
            aria-label={m.signupMoveUp()}
            onClick={() => onMove(-1)}
          >
            <ArrowUp />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            disabled={last}
            aria-label={m.signupMoveDown()}
            onClick={() => onMove(1)}
          >
            <ArrowDown />
          </Button>
          <Button variant="ghost" size="icon-sm" aria-label={m.signupRemove()} onClick={onRemove}>
            <Trash2 />
          </Button>
        </div>
      </div>
    </div>
  )
}

function RegistrationList({ businessId, eventId }: { businessId: string; eventId: string }) {
  const list = useEventRegistrations(businessId, eventId, true)
  const exportCsv = useExportEventRegistrations(businessId, eventId)

  const download = () =>
    exportCsv.mutate(undefined, {
      onSuccess: (blob) => {
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `anmeldungen-${eventId}.csv`
        link.click()
        URL.revokeObjectURL(url)
      },
      onError: (error) => toast.error(errorMessage(error)),
    })

  return (
    <div className="space-y-3 border-border border-t pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-medium text-sm">{m.signupList()}</p>
          {list.data ? (
            <p className="text-muted-foreground text-sm">
              {m.signupCounts({
                confirmed: String(list.data.counts.confirmed),
                waitlist: String(list.data.counts.waitlist),
              })}
            </p>
          ) : null}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={exportCsv.isPending || !list.data?.registrations.length}
          onClick={download}
        >
          <Download />
          {m.signupExport()}
        </Button>
      </div>
      {list.data && list.data.registrations.length === 0 ? (
        <p className="text-muted-foreground text-sm">{m.signupEmpty()}</p>
      ) : null}
      {list.data ? (
        <ul className="divide-y divide-border">
          {list.data.registrations.map((registration) => (
            <RegistrationRow
              key={registration.id}
              businessId={businessId}
              eventId={eventId}
              registration={registration}
              questions={list.data.questions}
            />
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function RegistrationRow({
  businessId,
  eventId,
  registration,
  questions,
}: {
  businessId: string
  eventId: string
  registration: EventRegistration
  questions: EventRegistrationsResponse['questions']
}) {
  const cancel = useCancelEventRegistration(businessId, eventId)
  const [confirming, setConfirming] = useState(false)
  const answered = questions.filter((question) => registration.answers[question.id] !== undefined)
  const cancelled = registration.status === 'cancelled'

  return (
    <li className="space-y-1.5 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium text-sm">{registration.name}</p>
          <p className="truncate text-muted-foreground text-sm">{registration.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={registration.status === 'confirmed' ? 'default' : 'outline'}>
            {STATUS_LABELS[registration.status]()}
          </Badge>
          {cancelled ? null : (
            <Button
              variant={confirming ? 'destructive' : 'ghost'}
              size="sm"
              disabled={cancel.isPending}
              onClick={() =>
                confirming
                  ? cancel.mutate(registration.id, {
                      onError: (error) => toast.error(errorMessage(error)),
                    })
                  : setConfirming(true)
              }
            >
              {confirming ? m.signupCancelConfirm() : m.signupCancel()}
            </Button>
          )}
        </div>
      </div>
      {answered.length > 0 ? (
        <dl className="grid gap-x-3 gap-y-0.5 text-sm sm:grid-cols-[minmax(0,12rem)_minmax(0,1fr)]">
          {answered.map((question) => (
            <div key={question.id} className="contents">
              <dt className="truncate text-muted-foreground">{question.label}</dt>
              <dd className="min-w-0 break-words">
                {answerLabel(registration.answers[question.id], m.signupYes(), m.signupNo())}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </li>
  )
}
